import React from 'react';
import { ClubOverviewSection } from './ClubOverviewSection';

function PerfTile({ label, value, sub, loading, valueClassName = 'text-text-primary' }) {
  return (
    <div className="flex flex-col gap-0.5">
      <p className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-wider text-text-muted leading-tight">
        {label}
      </p>
      {loading ? (
        <div className="mt-1.5 h-7 w-16 rounded bg-border-medium/40 animate-pulse" />
      ) : (
        <>
          <p className={`text-xl sm:text-2xl font-bold tabular-nums leading-none mt-1 ${valueClassName}`}>
            {value ?? '—'}
          </p>
          {sub && (
            <p className="text-[11px] text-text-muted leading-snug mt-0.5">
              {sub}
            </p>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Light KPI grid for My Club overview — shows the current player's performance at this club.
 *
 * @param {Object}     recap           - { mixed, levels, levelsLadder } from overviewRecap
 * @param {boolean}    recapLoading
 * @param {{ winRatePct: number|null, wins: number, gamesPlayed: number }|null} seasonRecord
 * @param {boolean}    seasonRecordLoading
 * @param {number|null} sessionsPlayed
 * @param {Object}     [selectedSeason] - { name }
 * @param {Function}   onViewStats     - navigates to Stats tab
 */
export function ClubPerformanceSnapshot({
  recap,
  recapLoading,
  seasonRecord,
  seasonRecordLoading = false,
  sessionsPlayed,
  selectedSeason,
  onViewStats,
}) {
  const mixed = recap?.mixed;
  const levels = recap?.levels;
  const levelsLadder = recap?.levelsLadder;

  const levelsLabel = (() => {
    if (levelsLadder === 'levels_female') return "Women's Elo";
    if (levelsLadder === 'levels_male') return "Men's Elo";
    return 'Doubles Elo';
  })();

  const wins = seasonRecord?.wins ?? 0;
  const gamesPlayed = seasonRecord?.gamesPlayed ?? 0;
  const winRatePct = gamesPlayed > 0 ? seasonRecord?.winRatePct ?? null : null;
  const seasonLabel = selectedSeason?.name ?? null;
  const levelsValueColor = levelsLadder === 'levels_female' ? 'text-female' : 'text-male';

  return (
    <ClubOverviewSection
      label="Your performance"
      subtitle={seasonLabel ? `${seasonLabel} season` : undefined}
      variant="performance"
      onViewAll={onViewStats}
      onSectionClick={onViewStats}
    >
      <div className="grid grid-cols-2 gap-x-6 gap-y-4 pt-1 sm:grid-cols-4 sm:gap-4">
        <PerfTile
          label="Mixed Elo"
          value={mixed?.elo_rating ?? null}
          sub={mixed?.rank != null ? `#${mixed.rank} ranked` : null}
          loading={recapLoading}
          valueClassName="text-female"
        />
        <PerfTile
          label={levelsLabel}
          value={levels?.elo_rating ?? null}
          sub={levels?.rank != null ? `#${levels.rank} ranked` : null}
          loading={recapLoading}
          valueClassName={levelsValueColor}
        />
        <PerfTile
          label="Win rate"
          value={winRatePct != null ? `${winRatePct}%` : null}
          sub={gamesPlayed > 0 ? `${wins}W / ${gamesPlayed - wins}L` : null}
          loading={seasonRecordLoading}
        />
        <PerfTile
          label="Sessions"
          value={sessionsPlayed}
          loading={recapLoading && sessionsPlayed === null}
        />
      </div>
    </ClubOverviewSection>
  );
}
